import React from 'react';
import { 
  Home, Calculator, Info, Video, Target, Users, PieChart, Briefcase, 
  User, UserCheck, Star, FileText, BarChart, TrendingUp, Activity, HelpCircle, Package, Wallet, Compass,
  FileSignature, Monitor, Calendar, CreditCard, MessageCircle, LayoutDashboard, Trophy,
  GraduationCap, ScanSearch
} from 'lucide-react';

import Startseite from '../pages/Startseite';
import ContactPage from '../pages/ContactPage';
import AcademyPage from '../pages/AcademyPage';
import ZielePage from '../pages/ZielePage';
import InfoPage from '../pages/InfoPage';
import PersonalDatenPage from '../pages/PersonalDatenPage';
import LohnfaktorPage from '../pages/LohnfaktorPage';
import KostenstrukturPage from '../pages/KostenstrukturPage';
import UnternehmerwertePage from '../pages/UnternehmerwertePage';
import LeistungPage from '../pages/LeistungPage';
import UnternehmerleistungSeite from '../pages/UnternehmerleistungSeite';
import GesamtleistungSeite from '../pages/GesamtleistungSeite';
import VertragsuebersichtPage from '../pages/VertragsuebersichtPage'; 
import KalkulatorPage from '../pages/KalkulatorPage';
import ExtensionskalkulatorPage from '../pages/ExtensionskalkulatorPage';
import InvestRechnerPage from '../pages/InvestRechnerPage';
import ProduktRechnerPage from '../pages/ProduktRechnerPage';
import BudgetPlanerPage from '../pages/BudgetPlanerPage';
import PrivatkostenKompassPage from '../pages/PrivatkostenKompassPage';
import AenderungsZentralePage from '../pages/AenderungsZentralePage';
import WebDesignPage from '../pages/WebDesignPage';
import ClientDashboard from '../pages/ClientDashboard';
import VorjahresKostenPage from '../pages/VorjahresKostenPage';
import DatenuebersichtPage from '../pages/DatenuebersichtPage';
import SystemBenchmarkView from '../pages/SystemBenchmarkView';
import HolyGrailDashboard from '../pages/HolyGrailDashboard';
import CoachNotesPage from '../pages/CoachNotesPage'; 
import EventDashboard from '../pages/EventDashboard';
import SeminarePage from '../pages/SeminarePage';
import PePapersPage from '../pages/PePapersPage';
import PEProfitPage from '../pages/PEProfitPage';
import PEStartPage from '../pages/PEStartPage';
import PERentPage from '../pages/PERentPage';
import PeScoutPage from '../pages/PeScoutPage';
import PeFactsPage from '../pages/PeFactsPage';
import PeCoachToolPlaceholderPage from '../pages/PeCoachToolPlaceholderPage';
import { isSoloModeActive } from '../utils/soloMode';

export const AppIconMap: Record<string, any> = {
  home: Home,
  calculator: Calculator,
  info: Info,
  video: Video,
  target: Target,
  user: User,
  briefcase: Briefcase,
  pieChart: PieChart,
  users: Users,
  userCheck: UserCheck,
  star: Star,
  barChart: BarChart,
  fileText: FileText,
  trendingUp: TrendingUp,
  activity: Activity,
  help: HelpCircle,
  package: Package,
  wallet: Wallet,
  compass: Compass,
  fileSignature: FileSignature,
  monitor: Monitor,
  calendar: Calendar,
  creditCard: CreditCard,
  message: MessageCircle,
  dashboard: LayoutDashboard,
  trophy: Trophy,
  academy: GraduationCap || Video,
  scanSearch: ScanSearch,
};

export interface AppPageConfig {
  key: string;
  label: string;
  path: string;
  description: string;
  component: React.ComponentType<any>;
  iconStr: string; 
  color: string;       
  borderClass: string; 
  iconClass?: string;  
  group: 'verwaltung' | 'basis' | 'organisation' | 'daten' | 'auswertung' | 'abschluss' | 'tools' | 'analyse'; 
  isVisible: boolean;
  isPremium?: boolean;
  layoutWidth?: string; 
  props?: any;
}

export const getAppPages = (
  clientDataIn?: any, 
  settings?: any, 
  onNavigate?: (page: string) => void
): AppPageConfig[] => {
  
  const clientData = clientDataIn?.clientData ? { ...clientDataIn, ...clientDataIn.clientData } : clientDataIn;
    
  const isBasicMode = clientData?.lizenzDetails?.typ === 'basic';
  const soloMode = isSoloModeActive(clientData);
  const visibilityMap = clientData?.pageVisibility || {};
  
  const isGodMode = !!clientData?._isGodMode || (typeof window !== 'undefined' && !!sessionStorage.getItem('GOD_MODE_ID'));
  /** Nur explizites Flag — God-Mode darf Sichtbarkeit NICHT „alles auf“ knipsen */
  const adminToggleOverride = !!clientData?._adminToggleOverride;
  const isUnlockedByAdmin = clientData?.isUnlockedByAdmin === true;
  /** Modul-Firewall nur per Flag umgehen — nicht automatisch durch God-Mode */
  const moduleFirewallBypass = adminToggleOverride;

  // 💉 CHIRURGIE: Desinfektion von LocalStorage "Ghost-Strings" ("null" vs null)
  const rawYearStr = typeof window !== 'undefined' ? localStorage.getItem('CURRENT_YEAR') : null;
  const currentYearStr = (rawYearStr && rawYearStr !== 'null' && rawYearStr !== 'undefined' && rawYearStr.trim() !== '') ? rawYearStr : null;
  
  const currentYearStatus = typeof window !== 'undefined' ? localStorage.getItem('CURRENT_YEAR_STATUS') : null;
  
  // angefragt = Mandant fertig, Coach prüft noch — gleiche Modul-Sperre wie Datenerfassung (NICHT aktiv)
  const isPreActivationStatus = (s: string | null | undefined) =>
      s === 'datenerfassung' || s === 'angefragt';

  let isDatenerfassung = isPreActivationStatus(currentYearStatus);
  let isAbgeschlossen = currentYearStatus === 'abgeschlossen';
  
  if (clientData?.geschaeftsjahre && currentYearStr) {
      const activeYear = clientData.geschaeftsjahre.find((j: any) => j.jahr.toString() === currentYearStr);
      if (activeYear) {
          isDatenerfassung = isPreActivationStatus(activeYear.status);
          isAbgeschlossen = activeYear.status === 'abgeschlossen';
      }
  }

  // App/Sidebar können den synchronisierten Status erzwingen (kein LocalStorage-Stale)
  if (typeof clientData?._forcedYearStatus === 'string' && clientData._forcedYearStatus) {
      isDatenerfassung = isPreActivationStatus(clientData._forcedYearStatus);
      isAbgeschlossen = clientData._forcedYearStatus === 'abgeschlossen';
  }

  /**
   * DATENERFASSUNG — harte Whitelist (Produktvorgabe).
   * Alles andere ist standardmäßig ZU. Admin-Toggle kann öffnen/schließen.
   * pe-papers, pe-profit, Änderungszentrale, Ziele, Tools, Auswertung, Analyse: NICHT hier.
   */
  const DATENERFASSUNG_ALLOWED = new Set([
      'jahresuebersicht',
      'info',
      'vertragsuebersicht',
      'kontakt',
      'academy',
      'events',
      'seminare',
      'startseite',
      'webdesign',
      'privatkosten-kompass',
      'unternehmer-werte',
      'kostenstruktur',
      'personal',
      'lohnfaktor',
  ]);

  /**
   * Datenerfassung — Defaults ohne Admin-Toggle (nur Startzustand).
   * Sobald Admin-PIN aktiv oder pageVisibility gesetzt: volle Steuerbarkeit, keine Sperre.
   */
  const DATENERFASSUNG_PERMANENT = new Set([
      'startseite',
      'webdesign',
      'privatkosten-kompass',
      'unternehmer-werte',
      'kostenstruktur',
      'lohnfaktor',
      'personal',
  ]);

  const employeePages: AppPageConfig[] = [];
  // leistung-* immer bauen wenn MA existieren — Sichtbarkeit steuert der Map-Block (nicht Solo-Bau-Stop)
  if (clientData?.personalData && Array.isArray(clientData.personalData)) {
    clientData.personalData.forEach((ma: any, index: number) => {
      if (ma.name) {
        const maKey = `leistung-${ma.id}`;
        employeePages.push({
          key: maKey, 
          label: `↳ ${ma.name}`,
          path: `/leistung/${ma.id}`,
          component: LeistungPage,
          iconStr: 'user',
          color: 'bg-[#CC8A84]',
          borderClass: 'border-[#CC8A84]',
          iconClass: 'text-[#CC8A84]',
          group: 'auswertung',
          isVisible: true,
          description: 'Mitarbeiter-Performance',
          layoutWidth: 'w-full',
          props: { employeeId: ma.id, isFirstEmployee: index === 0 }
        });
      }
    });
  }

  const allPages: AppPageConfig[] = [
    {
      key: 'jahresuebersicht',
      label: 'Jahres-Akte',
      path: '/',
      description: 'Verwalte hier deine Steuerjahre.',
      component: ClientDashboard,
      iconStr: 'calendar',
      color: 'bg-purple-500', 
      borderClass: 'border-purple-500',
      iconClass: 'text-purple-500',
      group: 'verwaltung',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { clientData, settings, onNavigate }
    },
    {
      key: 'info',
      label: 'Info & Erklärung',
      path: '/info',
      description: 'Grundlagen, Team & Legenden.',
      component: InfoPage,
      iconStr: 'info',
      color: 'bg-purple-500', 
      borderClass: 'border-purple-500',
      iconClass: 'text-purple-500',
      group: 'verwaltung', 
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'vertragsuebersicht',
      label: 'Vertragsübersicht',
      path: '/vertragsuebersicht',
      description: 'Zahlungen, Konditionen & Vertrag.',
      component: VertragsuebersichtPage,
      iconStr: 'fileSignature',
      color: 'bg-purple-500',
      borderClass: 'border-purple-500',
      iconClass: 'text-purple-500',
      group: 'verwaltung',
      isVisible: true,
      layoutWidth: 'max-w-7xl', 
      props: { }
    },
    {
      key: 'kontakt',
      label: 'Kontakt & Meetings',
      path: '/kontakt',
      description: 'Ansprechpartner & Termine.',
      component: ContactPage,
      iconStr: 'message',
      color: 'bg-purple-500', 
      borderClass: 'border-purple-500',
      iconClass: 'text-purple-500',
      group: 'verwaltung',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'webdesign',
      label: 'PEWeb - Webdesign',
      path: '/webdesign',
      description: 'PEWeb — Website Kalkulator.',
      component: WebDesignPage,
      iconStr: 'monitor',
      color: 'bg-purple-500',
      borderClass: 'border-purple-500',
      iconClass: 'text-purple-400',
      group: 'verwaltung',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'academy',
      label: 'Academy',
      path: '/academy',
      description: 'Video-Anleitungen & Wissen.',
      component: AcademyPage,
      iconStr: 'academy',
      color: 'bg-purple-500', 
      borderClass: 'border-purple-500',
      iconClass: 'text-purple-500',
      group: 'verwaltung',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'events',
      label: 'Kalender/Events',
      path: '/events',
      description: 'Kalender und Events — Sessions, Termine und Anmeldungen.',
      component: EventDashboard,
      iconStr: 'calendar',
      color: 'bg-purple-500', 
      borderClass: 'border-purple-500',
      iconClass: 'text-purple-500',
      group: 'verwaltung',
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'seminare',
      label: 'Geplante Seminare',
      path: '/seminare',
      description: 'Kommende Präsenz- & Online-Seminare mit Anmeldung zum Mandantenpreis.',
      component: SeminarePage,
      iconStr: 'star',
      color: 'bg-purple-500',
      borderClass: 'border-purple-500',
      iconClass: 'text-[#E6C299]',
      group: 'verwaltung',
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'pe-papers',
      label: 'PEPapers',
      path: '/pe-papers',
      description: 'Der automatisierte Dokumenten-Master für Mitarbeiterführung & Rechtssicherheit im Salon.',
      component: PePapersPage,
      iconStr: 'fileText',
      color: 'bg-amber-600',
      borderClass: 'border-amber-600',
      iconClass: 'text-amber-500',
      group: 'verwaltung',
      isVisible: true,
      isPremium: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'pe-profit',
      label: 'PE Profit',
      path: '/pe-profit',
      description: 'Bankenkonforme Rentabilitätsvorschau und Zukunftskalkulation für deinen Salon.',
      component: PEProfitPage,
      iconStr: 'trendingUp',
      color: 'bg-amber-600',
      borderClass: 'border-amber-600',
      iconClass: 'text-amber-500',
      group: 'analyse',
      isVisible: true,
      isPremium: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'pe-start',
      label: 'PEStart',
      path: '/pe-start',
      description:
        'Businessplan und Planungsanalyse für Existenzgründer und Salonübernahmen.',
      component: PEStartPage,
      iconStr: 'trendingUp',
      color: 'bg-amber-600',
      borderClass: 'border-amber-600',
      iconClass: 'text-amber-500',
      group: 'analyse',
      isVisible: true,
      isPremium: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'pe-rent',
      label: 'PERent',
      path: '/pe-rent',
      description:
        'Stuhlmiete- und Platzvermietung auf Basis deiner echten Salon-Gemeinkosten und Teamstunden.',
      component: PERentPage,
      iconStr: 'calculator',
      color: 'bg-amber-600',
      borderClass: 'border-amber-600',
      iconClass: 'text-amber-500',
      group: 'analyse',
      isVisible: true,
      isPremium: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'pe-plan',
      label: 'PEPlan',
      path: '/pe-plan',
      description:
        'Vollwertiges Simulationswerkzeug für Inhaber, um Personalfluktuation, Abgänge mit Auffangquote, Inhaber-Rückzug, Zusatzinvestitionen und punktgenaue Preisanpassungen vorab risikofrei im Salon durchzuspielen.',
      component: PeCoachToolPlaceholderPage,
      iconStr: 'dashboard',
      color: 'bg-amber-600',
      borderClass: 'border-amber-600',
      iconClass: 'text-amber-500',
      // Analyse neben PE Profit / PEStart / PERent (Admin-Katalog + Jahresmenü)
      group: 'analyse',
      // Registry offen; Kauf-Gate in Sidebar/Startseite (unlockedPremiumTools)
      isVisible: true,
      isPremium: true,
      layoutWidth: 'max-w-7xl',
      props: { toolId: 'pe-plan', mode: 'premium' }
    },
    {
      key: 'pe-scout',
      label: 'PEScout',
      path: '/pe-scout',
      description:
        'Mitarbeiterakquise: Masterclass, KI-Stellenanzeigen, Bild-KI und Interview-Navigator.',
      component: PeScoutPage,
      iconStr: 'scanSearch',
      color: 'bg-amber-600',
      borderClass: 'border-amber-600',
      iconClass: 'text-amber-500',
      group: 'verwaltung',
      isVisible: true,
      isPremium: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'pe-facts',
      label: 'PEFacts',
      path: '/pe-facts',
      description:
        'Masterclass & KI-Aushang-Generator für Preisanpassungen (No-Sorry-Strategie).',
      component: PeFactsPage,
      iconStr: 'trendingUp',
      color: 'bg-amber-600',
      borderClass: 'border-amber-600',
      iconClass: 'text-amber-500',
      group: 'verwaltung',
      isVisible: true,
      isPremium: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'planungsrechner',
      label: 'Planungsrechner',
      path: '/planungsrechner',
      description:
        'Arbeitstool Planungsrechner (Gott-Modus) — Szenario-Simulation für Personal, Inhaber, Kosten und Preise ohne Premium-Sperre.',
      component: PeCoachToolPlaceholderPage,
      iconStr: 'dashboard',
      color: 'bg-lime-600',
      borderClass: 'border-lime-600',
      iconClass: 'text-lime-500',
      group: 'tools',
      isVisible: isGodMode === true,
      isPremium: false,
      layoutWidth: 'max-w-7xl',
      props: { toolId: 'planungsrechner', mode: 'god' }
    },
    {
      key: 'pe-solo',
      label: 'Solo-Simulator',
      path: '/pe-solo',
      description:
        'Arbeitstool Solisten-Simulation — Standard aus, nur per Admin-Freischaltung sichtbar.',
      component: PeCoachToolPlaceholderPage,
      iconStr: 'user',
      // Eigenes Grün (nicht emerald-900 der Standard-Arbeitstools) = spezielles Admin-Tool
      color: 'bg-lime-600',
      borderClass: 'border-lime-600',
      iconClass: 'text-lime-500',
      group: 'tools',
      isVisible: false,
      isPremium: false,
      layoutWidth: 'max-w-7xl',
      props: { toolId: 'pe-solo' }
    },
    {
      key: 'startseite',
      label: 'System-Startseite',
      path: '/start',
      description: 'System-Übersicht.', 
      component: Startseite,
      iconStr: 'home',
      color: 'bg-slate-500', 
      borderClass: 'border-slate-500',
      iconClass: 'text-slate-500',
      group: 'basis',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { clientData, settings, onNavigate }
    },
    {
      key: 'coach-akte',
      label: 'Coach Akte',
      path: '/coach-akte',
      description: 'Interne Notizen (Streng Vertraulich).',
      component: CoachNotesPage,
      iconStr: 'briefcase', 
      color: 'bg-red-600', 
      borderClass: 'border-red-600',
      iconClass: 'text-red-600',
      group: 'basis',
      isVisible: isGodMode, 
      layoutWidth: 'max-w-4xl',
      props: { clientData }
    },
    {
      key: 'ziele',
      label: 'Ziele',
      path: '/ziele',
      description: 'Strategie und Vision.',
      component: ZielePage,
      iconStr: 'target',
      color: 'bg-emerald-500', 
      borderClass: 'border-emerald-500', 
      iconClass: 'text-emerald-500',
      group: 'basis',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'aenderungs-zentrale',
      label: 'Änderungs-Zentrale',
      path: '/aenderungs-zentrale',
      description: 'Verwaltung von Änderungen.',
      component: AenderungsZentralePage,
      iconStr: 'fileSignature',
      color: 'bg-sky-500',
      borderClass: 'border-sky-500',
      iconClass: 'text-sky-500',
      group: 'organisation',
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'privatkosten-kompass',
      label: 'Privatkosten-Kompass',
      path: '/privatkosten-kompass',
      description: 'Private Ausgaben optimieren.',
      component: PrivatkostenKompassPage,
      iconStr: 'compass',
      color: 'bg-sky-500',
      borderClass: 'border-sky-500',
      iconClass: 'text-sky-500',
      group: 'organisation',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'unternehmer-werte',
      label: 'Unternehmer-Werte',
      path: '/unternehmer-werte',
      description: 'Öffnungszeiten und Lohnziele.',
      component: UnternehmerwertePage,
      iconStr: 'star',
      color: 'bg-blue-600',
      borderClass: 'border-blue-600',
      iconClass: 'text-blue-600',
      group: 'daten',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'kostenstruktur',
      label: 'Kosten-Struktur',
      path: '/kostenstruktur',
      description: 'BWA und Fixkosten.',
      component: KostenstrukturPage,
      iconStr: 'pieChart',
      color: 'bg-blue-600',
      borderClass: 'border-blue-600',
      iconClass: 'text-blue-600',
      group: 'daten',
      isVisible: true,
      layoutWidth: 'max-w-7xl', 
      props: { }
    },
    {
      key: 'personal',
      label: 'Personal-Daten',
      path: '/personal',
      description: 'Stammdaten und Leistungslöhne.',
      component: PersonalDatenPage,
      iconStr: 'users',
      color: 'bg-blue-600',
      borderClass: 'border-blue-600',
      iconClass: 'text-blue-600',
      group: 'daten',
      isVisible: !isBasicMode && !soloMode,
      layoutWidth: 'w-full', 
      props: { }
    },
    {
      key: 'lohnfaktor',
      label: 'Lohn-Faktor',
      path: '/lohnfaktor',
      description: 'Lohnfaktor und Kostendeckung.',
      component: LohnfaktorPage,
      iconStr: 'barChart',
      color: 'bg-blue-600',
      borderClass: 'border-blue-600',
      iconClass: 'text-blue-600',
      group: 'daten', 
      isVisible: true, 
      layoutWidth: 'w-full',
      props: { }
    },

    ...employeePages, 
    
    {
      key: 'unternehmer-leistung',
      label: 'Unternehmer-Leistung',
      path: '/unternehmer-leistung',
      description: 'Eigene Performance Analyse.',
      component: UnternehmerleistungSeite,
      iconStr: 'userCheck',
      color: 'bg-amber-500',
      borderClass: 'border-amber-500', 
      iconClass: 'text-amber-500',
      group: 'auswertung',
      isVisible: !isBasicMode,
      layoutWidth: 'w-full', 
      props: { }
    },
    {
      key: 'gesamtleistung',
      label: 'Gesamt-Leistung',
      path: '/ergebnis',
      description: 'Unternehmens-Status Überblick.',
      component: GesamtleistungSeite,
      iconStr: 'activity',
      color: 'bg-[#E6C299]',
      borderClass: 'border-[#E6C299]', 
      iconClass: 'text-[#E6C299]',
      group: 'auswertung',
      isVisible: true,
      layoutWidth: 'w-full', 
      props: { }
    },
    {
      key: 'holy-grail',
      label: 'Business Cockpit', 
      path: '/holy-grail',
      description: 'High-End CEO Strategy & Performance Dashboard.',
      component: HolyGrailDashboard,
      iconStr: 'trophy',
      color: 'bg-[#E6C299]',
      borderClass: 'border-[#E6C299]',
      iconClass: 'text-[#E6C299]',
      group: 'analyse',
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'datenuebersicht',
      label: 'Datenübersicht',
      path: '/datenuebersicht',
      description: 'Zentrales Steuerungs-Cockpit für den Inhaber.',
      component: DatenuebersichtPage,
      iconStr: 'dashboard',
      color: 'bg-[#E6C299]',
      borderClass: 'border-[#E6C299]',
      iconClass: 'text-[#E6C299]',
      group: 'analyse',
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'system-benchmark',
      label: 'SystemBenchmark',
      path: '/system-benchmark',
      description: '3-Spalten-Benchmark: Mein Salon · Peer (±20 %) · Gesamtmarkt.',
      component: SystemBenchmarkView,
      iconStr: 'barChart',
      color: 'bg-[#dcb68a]',
      borderClass: 'border-[#dcb68a]',
      iconClass: 'text-[#dcb68a]',
      group: 'analyse',
      isVisible: true,
      layoutWidth: 'max-w-7xl',
      props: { }
    },
    {
      key: 'budget-planer',
      label: 'Budget-Planer',
      path: '/budget-planer',
      description: 'Budget Planung.',
      component: BudgetPlanerPage,
      iconStr: 'wallet',
      color: 'bg-[#E6C299]',
      borderClass: 'border-[#E6C299]',
      iconClass: 'text-[#E6C299]',
      group: 'analyse',
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'vorjahres-kosten',
      label: 'Vorjahres-Kosten',
      path: '/vorjahres-kosten',
      description: 'Vergleich der aktuellen Kosten mit dem Vorjahr.',
      component: VorjahresKostenPage,
      iconStr: 'barChart',
      color: 'bg-[#E6C299]', 
      borderClass: 'border-[#E6C299]',
      iconClass: 'text-[#E6C299]',
      group: 'analyse',
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'invest-rechner', 
      label: 'Invest-Rechner',
      path: '/InvestRechner',
      description: 'ROI Berechnung.',
      component: InvestRechnerPage,
      iconStr: 'trendingUp',
      color: 'bg-emerald-900', 
      borderClass: 'border-emerald-900',
      iconClass: 'text-emerald-900',
      group: 'tools', 
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'kalkulator', 
      label: 'Kalkulator',
      path: '/kalkulator',
      description: 'Dienstleistungen kalkulieren.',
      component: KalkulatorPage,
      iconStr: 'calculator',
      color: 'bg-emerald-900', 
      borderClass: 'border-emerald-900',
      iconClass: 'text-emerald-900',
      group: 'tools', 
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'extensionskalkulator', 
      label: 'Extensions-Kalkulator',
      path: '/extensionskalkulator',
      description: 'Materialaufwand Chirurgie.',
      component: ExtensionskalkulatorPage,
      iconStr: 'calculator',
      color: 'bg-emerald-900', 
      borderClass: 'border-emerald-900',
      iconClass: 'text-emerald-900',
      group: 'tools', 
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    },
    {
      key: 'produkt-rechner', 
      label: 'Produkt-Rechner',
      path: '/produkt-rechner',
      description: 'Produktpreise kalkulieren.',
      component: ProduktRechnerPage,
      iconStr: 'package',
      color: 'bg-emerald-900', 
      borderClass: 'border-emerald-900',
      iconClass: 'text-emerald-900',
      group: 'tools', 
      isVisible: true,
      layoutWidth: 'w-full',
      props: { }
    }
  ];

  return allPages.map(page => {
      const storageKey = isDatenerfassung ? `${page.key}_datenerfassung` : 
                         isAbgeschlossen ? `${page.key}_abgeschlossen` : 
                         page.key;
                         
      const hasExplicitToggle = visibilityMap.hasOwnProperty(storageKey);
      const explicitValue = visibilityMap[storageKey];
      
      let finalVisibility = page.isVisible;

      // Coach-Akte: NUR God-Mode / Coach-Admin — niemals Mandant (sensible Notizen)
      if (page.key === 'coach-akte') {
          return { ...page, isVisible: !!isGodMode };
      }
      // Solo-Simulator: ausschließlich Gott-Modus (kein Mandanten-Toggle, kein pageVisibility)
      if (page.key === 'pe-solo') {
        return { ...page, isVisible: isGodMode === true };
      }
      // Planungsrechner (Intern): Strikt NUR im Gott-Modus, niemals Mandantenansicht
      if (page.key === 'planungsrechner') {
        return { ...page, isVisible: isGodMode === true };
      }
      // Premium-Tools (inkl. PEPlan): nicht von Datenerfassung-Whitelist ausblenden —
      // Sichtbarkeit für Mandanten steuert nur unlockedPremiumTools (Sidebar/Startseite).
      const isPremiumUnlockPage =
          page.key === 'pe-papers' ||
          page.key === 'pe-profit' ||
          page.key === 'pe-start' ||
          page.key === 'pe-rent' ||
          page.key === 'pe-scout' ||
          page.key === 'pe-facts' ||
          page.key === 'pe-plan';
      if (isPremiumUnlockPage) {
          // Immer registry-offen — Sichtbarkeit steuert ausschließlich unlockedPremiumTools
          // (pageVisibility darf freigeschaltete Premium-Tools nicht verstecken)
          return { ...page, isVisible: true };
      }

      // ═══ DATENERFASSUNG ═══
      // Standard für Mandanten: Whitelist + Pflichtseiten offen.
      // Mit Admin-PIN (_adminToggleOverride) oder gesetztem pageVisibility: volle Kontrolle — keine Sperren.
      if (isDatenerfassung) {
          const isPflichtOderArbeitsdaten =
              DATENERFASSUNG_PERMANENT.has(page.key) || page.group === 'daten';

          // Passwort-Admin / Coach: jeder Toggle zählt, nichts ist „unantastbar“
          if (adminToggleOverride) {
              if (hasExplicitToggle) {
                  return { ...page, isVisible: !!explicitValue };
              }
              // Noch kein Toggle gesetzt → sinnvolle Defaults
              if (isPflichtOderArbeitsdaten) {
                  return { ...page, isVisible: true };
              }
              return { ...page, isVisible: DATENERFASSUNG_ALLOWED.has(page.key) };
          }

          // Mandanten-Ansicht: gesetzte Admin-Toggles gelten trotzdem (was Admin ausblendet, bleibt aus)
          if (hasExplicitToggle) {
              return { ...page, isVisible: !!explicitValue };
          }
          if (isPflichtOderArbeitsdaten) {
              return { ...page, isVisible: true };
          }

          finalVisibility = DATENERFASSUNG_ALLOWED.has(page.key);
          if (
              !soloMode ||
              !page.key.startsWith('leistung-')
          ) {
              return { ...page, isVisible: !!finalVisibility };
          }
          return { ...page, isVisible: false };
      }

      // 💉 Diese Gruppen erfordern zwingend eine aktive Jahresakte
      const yearDependentGroups = ['daten', 'auswertung', 'analyse', 'tools'];

      // 2. Filterung anwenden
      if (page.group === 'verwaltung') {
          // Premium-Tools: sichtbar im Registry; Sidebar/Startseite filtern nach unlockedPremiumTools
          finalVisibility = true;
      } 
      else if (!currentYearStr) {
          // 💉 KEIN JAHR AKTIV: Wir verriegeln das komplette Fach-Menü
          if (yearDependentGroups.includes(page.group)) {
              finalVisibility = false;
          } else {
              // Basis & Organisation bleiben sichtbar (z.B. Startseite, Ziele)
              // Webdesign liegt in verwaltung → unabhängig vom Geschäftsjahr
              finalVisibility = page.isVisible;
          }
      }
      else if (isAbgeschlossen) {
          if (['daten', 'tools'].includes(page.group) || ['budget-planer', 'aenderungs-zentrale'].includes(page.key)) {
              finalVisibility = false;
          } else {
              finalVisibility = page.isVisible;
          }
      }

      // 3. Custom-Toggles aus pageVisibility
      if (hasExplicitToggle) {
          if (adminToggleOverride || currentYearStr || !yearDependentGroups.includes(page.group) || explicitValue === false) {
              finalVisibility = explicitValue;
          }
      }

      // Solo-Default: Personal/Leistung zu — explizite Admin-Freigabe (pageVisibility) bleibt wirksam
      if (
          !adminToggleOverride &&
          soloMode &&
          (page.key === 'personal' || page.key.startsWith('leistung-')) &&
          !hasExplicitToggle
      ) {
          finalVisibility = false;
      }

      if (adminToggleOverride && hasExplicitToggle) {
          finalVisibility = explicitValue;
      }

  // Modul-Firewall außerhalb Datenerfassung: ohne Account-Freigabe gesperrt
  // God-Mode darf isUnlockedByAdmin NICHT faken — sonst sieht Admin alles offen
  // pe-papers / pe-profit / pe-start / pe-rent / pe-scout / pe-facts: Kauf-Gate in Sidebar (unlockedPremiumTools), nicht Modul-Firewall
  const isPremiumToolPage =
      page.key === 'pe-papers' ||
      page.key === 'pe-profit' ||
      page.key === 'pe-start' ||
      page.key === 'pe-rent' ||
      page.key === 'pe-scout' ||
      page.key === 'pe-facts' ||
      page.key === 'pe-plan';
  const isFirewalledModule =
      page.group === 'tools' ||
      page.group === 'auswertung' ||
      page.group === 'analyse' ||
      page.group === 'abschluss';
  if (
      isFirewalledModule &&
      !isPremiumToolPage &&
      page.key !== 'planungsrechner' &&
      !moduleFirewallBypass &&
      !isUnlockedByAdmin
  ) {
      finalVisibility = false;
  }

  return { ...page, isVisible: !!finalVisibility };
  });
};

export default getAppPages;